import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Mail, Shield, Trash2, ToggleLeft, ToggleRight, AlertTriangle, Database, KeyRound, Eye, EyeOff } from "lucide-react";
import api from "../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ROLE_COLORS = {
  admin:   "bg-red-100 text-red-800 border-red-200",
  manager: "bg-blue-100 text-blue-800 border-blue-200",
  user:    "bg-gray-100 text-gray-800 border-gray-200",
};

const CLEAR_OPTIONS = [
  { value: 'activity_logs', label: 'Activity Logs', desc: 'Delete all activity log records' },
  { value: 'tasks_done',    label: 'Completed Tasks', desc: 'Delete all tasks marked as done' },
];

const UserManagement = ({ user }) => {
  const [users, setUsers]               = useState([]);
  const [newUser, setNewUser]           = useState({ email: "", role: "user" });
  const [inviteOpen, setInviteOpen]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // user to delete
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearTarget, setClearTarget]   = useState('activity_logs');
  // Password change state
  const [pwSheet, setPwSheet]           = useState(null); // null | { user, step: 'send'|'verify' }
  const [otp, setOtp]                   = useState('');
  const [newPassword, setNewPassword]   = useState('');
  const [showPw, setShowPw]             = useState(false);
  const [pwLoading, setPwLoading]       = useState(false);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const r = await api.get('/api/users');
      setUsers(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch users");
    }
  };

  useEffect(() => { fetchUsers(); }, []); // eslint-disable-line

  const adminCount = users.filter(u => u.role === 'admin' && u.is_active).length;
  const isLastAdmin = (u) => u.role === 'admin' && adminCount <= 1;
  const canDelete   = (u) => u.id !== user.id && !isLastAdmin(u);
  const canToggle   = (u) => u.id !== user.id && !(u.role === 'admin' && u.is_active && adminCount <= 1);
  const canChangeRole = (u) => u.id !== user.id && !(u.role === 'admin' && adminCount <= 1 && u.role === 'admin');

  const createUser = async () => {
    if (!newUser.email) { toast.error("Email is required"); return; }
    try {
      await api.post('/api/auth/create-user', newUser);
      toast.success(`Invitation sent to ${newUser.email}`);
      setNewUser({ email: "", role: "user" });
      setInviteOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const deleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/users/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.email}`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const toggleUser = async (u) => {
    try {
      await api.patch(`/api/users/${u.id}/toggle`);
      toast.success(`${u.is_active ? 'Deactivated' : 'Activated'} ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle user");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/api/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${role}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change role");
    }
  };

  const openPwSheet = (u) => {
    setOtp(''); setNewPassword(''); setShowPw(false);
    setPwSheet({ user: u, step: 'send' });
  };

  const sendOtp = async () => {
    setPwLoading(true);
    try {
      await api.post(`/api/users/${pwSheet.user.id}/send-otp`);
      toast.success(`OTP sent to ${pwSheet.user.email}`);
      setPwSheet(d => ({ ...d, step: 'verify' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally { setPwLoading(false); }
  };

  const changePassword = async () => {
    if (!otp.trim() || !newPassword.trim()) { toast.error("OTP and new password are required"); return; }
    setPwLoading(true);
    try {
      await api.post(`/api/users/${pwSheet.user.id}/change-password`, { otp, new_password: newPassword });
      toast.success(`Password changed for ${pwSheet.user.email}`);
      setPwSheet(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setPwLoading(false); }
  };

  const clearData = async () => {
    try {
      const r = await api.post('/api/users/clear-data', { target: clearTarget });
      toast.success(r.data.message);
      setClearConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear data");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage team access and roles</p>
          </div>
          <Button data-testid="add-user-button" className="bg-gray-900 hover:bg-gray-800" onClick={() => setInviteOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Users table */}
        <Card className="bg-white border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['User', 'Email', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900 text-sm">
                    {u.name || <span className="text-gray-400 italic">Not set</span>}
                    {u.id === user.id && <span className="ml-2 text-xs text-indigo-500 font-normal">(you)</span>}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">{u.email}</td>

                  {/* Role — inline change for admin */}
                  <td className="px-5 py-3">
                    {canChangeRole(u) ? (
                      <Select value={u.role} onValueChange={role => changeRole(u, role)}>
                        <SelectTrigger className={`h-7 text-xs border px-2 w-28 ${ROLE_COLORS[u.role]}`}>
                          <Shield className="w-3 h-3 mr-1 shrink-0" /><SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${ROLE_COLORS[u.role]} border text-xs`}>
                        <Shield className="w-3 h-3 mr-1" />{u.role}
                        {isLastAdmin(u) && <span className="ml-1 text-xs opacity-60">(last)</span>}
                      </Badge>
                    )}
                  </td>

                  <td className="px-5 py-3">
                    <Badge className={u.is_active
                      ? "bg-green-100 text-green-800 border-green-200 border text-xs"
                      : "bg-gray-100 text-gray-600 border-gray-200 border text-xs"}>
                      {u.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>

                  <td className="px-5 py-3 text-xs text-gray-400">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>

                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {/* Toggle active */}
                      {canToggle(u) && (
                        <button title={u.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleUser(u)}
                          className={`transition-colors ${u.is_active ? 'text-green-500 hover:text-red-400' : 'text-gray-400 hover:text-green-500'}`}>
                          {u.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        </button>
                      )}
                      {/* Change password */}
                      {u.id !== user.id && (
                        <button title="Change password" onClick={() => openPwSheet(u)}
                          className="text-zinc-300 hover:text-indigo-500 transition-colors">
                          <KeyRound size={15} />
                        </button>
                      )}
                      {/* Delete */}
                      {canDelete(u) ? (
                        <button data-testid={`delete-user-${u.id}`} title="Delete user"
                          onClick={() => setDeleteTarget(u)}
                          className="text-zinc-300 hover:text-red-500 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      ) : u.id !== user.id && (
                        <span title={isLastAdmin(u) ? 'Last admin — cannot delete' : ''}>
                          <Trash2 size={16} className="text-zinc-100 cursor-not-allowed" />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No users found.</p>}
        </Card>

        {/* Admin — Clear Data */}
        <Card className="bg-white border border-red-100 p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-50 rounded-lg mt-0.5">
              <Database size={16} className="text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900">Clear Data</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Permanently delete records from the database. This cannot be undone.</p>
              <div className="flex items-center gap-3">
                <Select value={clearTarget} onValueChange={setClearTarget}>
                  <SelectTrigger className="w-52 text-sm border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLEAR_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        <div>
                          <div className="font-medium">{o.label}</div>
                          <div className="text-xs text-gray-400">{o.desc}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 text-sm"
                  onClick={() => setClearConfirmOpen(true)}>
                  <AlertTriangle size={14} className="mr-1.5" /> Clear
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Invite User Sheet ─────────────────────────────────────────────── */}
      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent side="right" className="w-[75vw] sm:max-w-[75vw] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>Invite New User</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input data-testid="user-email-input" type="email" placeholder="user@example.com"
                value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1 border-gray-300" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1 border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <Mail className="w-4 h-4 inline mr-1" />
              A one-time password will be emailed to this address.
            </div>
            <Button data-testid="create-user-button" onClick={createUser} className="w-full bg-gray-900 hover:bg-gray-800">
              Send Invitation
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete User Confirm Sheet ─────────────────────────────────────── */}
      <Sheet open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={18} /> Delete User
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Delete <strong>{deleteTarget?.email}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              This permanently removes the user. Their leads and tasks remain.
            </p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={deleteUser}>Delete</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Clear Data Confirm Sheet ──────────────────────────────────────── */}
      <Sheet open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle size={18} /> Confirm Clear
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              This will permanently delete all <strong>{CLEAR_OPTIONS.find(o => o.value === clearTarget)?.label}</strong>.
            </p>
            <p className="text-sm text-gray-500">This action cannot be undone.</p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={clearData}>Yes, Clear</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Change Password Sheet ─────────────────────────────────────────── */}
      <Sheet open={!!pwSheet} onOpenChange={() => setPwSheet(null)}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center gap-2">
              <KeyRound size={16} className="text-indigo-500" /> Change Password
            </SheetTitle>
          </SheetHeader>

          {pwSheet?.step === 'send' ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                An OTP will be sent to <strong>{pwSheet.user.email}</strong> to verify this password change.
              </p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setPwSheet(null)}>Cancel</Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={sendOtp} disabled={pwLoading}>
                  {pwLoading ? 'Sending...' : 'Send OTP'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">OTP sent to <strong>{pwSheet?.user.email}</strong>. Valid for 10 minutes.</p>
              <div>
                <Label>Enter OTP</Label>
                <Input placeholder="6-digit OTP" value={otp} onChange={e => setOtp(e.target.value)}
                  className="mt-1 border-gray-300 tracking-widest text-center text-lg" maxLength={6} />
              </div>
              <div>
                <Label>New Password</Label>
                <div className="relative mt-1">
                  <Input type={showPw ? 'text' : 'password'} placeholder="Min 8 characters"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    className="border-gray-300 pr-9" />
                  <button type="button" onClick={() => setShowPw(s => !s)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2 justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={sendOtp} disabled={pwLoading} className="text-xs text-gray-400">
                  Resend OTP
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPwSheet(null)}>Cancel</Button>
                  <Button onClick={changePassword} disabled={pwLoading} className="bg-indigo-600 hover:bg-indigo-700">
                    {pwLoading ? 'Saving...' : 'Change Password'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default UserManagement;
